import React, { useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
  Paper, InputAdornment, Popover, MenuItem,
  ListItemText, Chip, Checkbox, Tooltip
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import { useNavigate } from "react-router-dom";
import "./style.css";

const CorporateUserDetailsComponent = ({
  corporateData,
  BPList,
  selectedRows,
  handleRowSelection,
  handleSelectAll,
  editOpen,
  setEditOpen,
  editedName,
  setEditedName,
  handleEditClick,
  handleSaveEdit,
  unlinkOpen,
  setUnlinkOpen,
  handleUnlinkClick,
  handleConfirmUnlink,
  searchValue,
  handleSearchChange,
  handleStatusToggle,
  currentStatusFilter,
  handleAddNewCorporateUser,
  handleScroll,
  loading,
  setEditedDepartmentName,
  editedDepartmentName,
  departments = [],
  deleteOpen,
  setDeleteOpen,
  handleDeleteClick,
  handleConfirmDelete,
  handleBulkDelete,
  activateOpen,
  setActivateOpen,
  handleActivateClick,
  handleConfirmActivate,
  handleBulkActivate,
  deactivateOpen,
  setDeactivateOpen,
  handleDeactivateClick,
  handleConfirmDeactivate,
  handleBulkDeactivate
}) => {
  const navigate = useNavigate();
  const [departmentFilterAnchorEl, setDepartmentFilterAnchorEl] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [bulkActionConfirmOpen, setBulkActionConfirmOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null);

  const availableDepartments = [...new Set(BPList
    .map(user => user.departmentName || 'Not Assigned')
    .filter(Boolean)
  )];

  const handleDepartmentFilterClick = (event) => {
    setDepartmentFilterAnchorEl(event.currentTarget);
  };

  const handleDepartmentFilterClose = () => {
    setDepartmentFilterAnchorEl(null);
  };

  const handleDepartmentSelect = (department) => {
    setSelectedDepartment(department);
    handleDepartmentFilterClose();
  };

  const handleBulkActionClick = (type) => {
    setBulkActionType(type);
    setBulkActionConfirmOpen(true);
  };

  const handleBulkActionConfirm = () => {
    if (bulkActionType === 'activate') {
      handleBulkActivate();
    } else if (bulkActionType === 'deactivate') {
      handleBulkDeactivate();
    }
    setBulkActionConfirmOpen(false);
    setBulkActionType(null);
  };

  const handleBulkActionCancel = () => {
    setBulkActionConfirmOpen(false);
    setBulkActionType(null);
  };

  const filteredUsers = selectedDepartment
    ? BPList.filter(user => 
        (user.departmentName || 'Not Assigned') === selectedDepartment
      )
    : BPList;

  const departmentFilterOpen = Boolean(departmentFilterAnchorEl);
  const departmentFilterId = departmentFilterOpen ? 'department-filter-popover' : undefined;

  return (
    <Box className="corp-user-mgmt-container">
      <Sidebar />
      <Box className="corp-user-mgmt-content">
        {/* Header Section */}
        <Box className="corp-user-mgmt-header-section">
          <button className="corp-user-mgmt-back-btn" onClick={() => navigate(-1)}>
            ← 
          </button>
          <Typography variant="h5" className="corp-user-mgmt-title">
            User Management - <span>
              {corporateData?.corporateName || 'Corporate Name'}
            </span>
          </Typography>
        </Box>

        {/* Controls Panel */}
        <Box className="corp-user-mgmt-controls-panel">
          <Box className="corp-user-mgmt-filter-group">
            <Box className="corp-user-mgmt-status-toggles">
              <Button
                className={`corp-user-mgmt-status-btn ${currentStatusFilter === 'active' ? 'active' : 'outlined'}`}
                variant={currentStatusFilter === 'active' ? 'contained' : 'outlined'}
                onClick={() => handleStatusToggle('active')}
              >
                Active 
              </Button>
              <Button
                className={`corp-user-mgmt-status-btn ${currentStatusFilter === 'inactive' ? 'inactive' : 'outlined'}`}
                variant={currentStatusFilter === 'inactive' ? 'contained' : 'outlined'}
                onClick={() => handleStatusToggle('inactive')}
              >
                Inactive 
              </Button>
            </Box>

            <TextField
              className="corp-user-mgmt-search-field"
              variant="outlined"
              size="small"
              placeholder="Search by ID or Name"
              value={searchValue}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
          </Box>

          <Box className="corp-user-mgmt-action-group">
            <Button
              className="corp-user-mgmt-add-btn"
              variant="contained"
              onClick={handleAddNewCorporateUser}
            >
              + Add New User
            </Button>
          </Box>
        </Box>

        {/* Bulk Action Bar */}
        {selectedRows.length > 0 && (
          <Box className="corp-user-mgmt-bulk-bar">
            <Box className="corp-user-mgmt-bulk-container">
              {currentStatusFilter === 'active' ? (
                <Button
                  className="corp-user-mgmt-bulk-btn deactivate"
                  variant="contained"
                  startIcon={<CancelIcon />}
                  onClick={() => handleBulkActionClick('deactivate')}
                  disabled={loading}
                >
                  Deactivate ({selectedRows.length})
                </Button>
              ) : (
                <Button
                  className="corp-user-mgmt-bulk-btn activate"
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleBulkActionClick('activate')}
                  disabled={loading}
                >
                  Activate ({selectedRows.length})
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Table Section */}
        <Box className="corp-user-mgmt-table-wrapper">
          <TableContainer component={Paper} className="corp-user-mgmt-table-container">
            <Table className="corp-user-mgmt-table">
              <TableHead className="corp-user-mgmt-table-header">
                <TableRow className="corp-user-mgmt-table-header-row">
                  <TableCell align="center" width="60">
                    <Checkbox
                      checked={selectedRows.length === filteredUsers.length && filteredUsers.length > 0}
                      indeterminate={selectedRows.length > 0 && selectedRows.length < filteredUsers.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell align="center">Employee ID</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">
                    <Box className="corp-user-mgmt-dept-header">
                      Department
                      <IconButton
                        size="small"
                        onClick={handleDepartmentFilterClick}
                        className="corp-user-mgmt-filter-icon"
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
            </Table>
            
            <Box className="corp-user-mgmt-table-scroll" onScroll={handleScroll}>
              <Table className="corp-user-mgmt-table">
                <TableBody>
                  {filteredUsers?.map((row, i) => (
                    <TableRow key={i} hover selected={selectedRows.includes(i)} className="corp-user-mgmt-table-row">
                      <TableCell align="center">
                        <Checkbox
                          checked={selectedRows.includes(i)}
                          onChange={() => handleRowSelection(i)}
                        />
                      </TableCell>
                      <TableCell align="center">{row.usersCorporateId || 'N/A'}</TableCell>
                      <TableCell align="center">{row.userName || 'N/A'}</TableCell>
                      <TableCell align="center">{row.emailId || 'N/A'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.status ? 'Active' : 'Inactive'}
                          size="small"
                          className={`corp-user-mgmt-status-chip ${row.status ? 'active' : 'inactive'}`}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">{row.departmentName || 'N/A'}</TableCell>
                      <TableCell align="center">
                        <Box className="corp-user-mgmt-actions">
                          <Tooltip title="Edit">
                            <IconButton 
                              onClick={() => handleEditClick(i)} 
                              size="small"
                              className="corp-user-mgmt-action-icon edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {row.status ? (
                            <Tooltip title="Deactivate">
                              <IconButton 
                                onClick={() => handleDeactivateClick(i)} 
                                size="small"
                                className="corp-user-mgmt-action-icon delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activate">
                              <IconButton 
                                onClick={() => handleActivateClick(i)} 
                                size="small"
                                className="corp-user-mgmt-action-icon activate"
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {row.unlinkRequest === true && (
                            <Tooltip title="Unlink">
                              <Button
                                onClick={() => handleUnlinkClick(i)}
                                size="small"
                                className="corp-user-mgmt-unlink-btn"
                                variant="outlined"
                              >
                                Unlink
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredUsers?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" className="corp-user-mgmt-empty-state">
                        <Typography className="corp-user-mgmt-empty-text">No users found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        </Box>

        {/* Department Filter Popover */}
        <Popover
          id={departmentFilterId}
          open={departmentFilterOpen}
          anchorEl={departmentFilterAnchorEl}
          onClose={handleDepartmentFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            className: "corp-user-mgmt-filter-popover"
          }}
        >
          <Box>
            <Typography variant="subtitle1" className="corp-user-mgmt-filter-title">
              Filter by Department
            </Typography>
            
            <MenuItem
              onClick={() => handleDepartmentSelect("")}
              className={`corp-user-mgmt-filter-item ${!selectedDepartment ? 'selected' : ''}`}
            >
              <ListItemText primary="All Departments" />
            </MenuItem>
            
            {availableDepartments.map((dept) => (
              <MenuItem
                key={dept}
                onClick={() => handleDepartmentSelect(dept)}
                className={`corp-user-mgmt-filter-item ${selectedDepartment === dept ? 'selected' : ''}`}
              >
                <ListItemText primary={dept} />
              </MenuItem>
            ))}
          </Box>
        </Popover>

        {/* Edit Dialog */}
        <Dialog 
          open={editOpen} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ className: "corp-user-mgmt-dialog" }}
        >
          <DialogTitle className="corp-user-mgmt-dialog-title">Edit User Details</DialogTitle>
          <DialogContent className="corp-user-mgmt-dialog-content">
            <TextField
              fullWidth
              label="Full Name"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              margin="dense"
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Department"
              value={editedDepartmentName}
              onChange={(e) => setEditedDepartmentName(e.target.value)}
              margin="dense"
              variant="outlined"
              // sx={{textAlign:"center"}}
              select
            >
              {departments.map((dept) => (
                <MenuItem key={dept} value={dept}>
                  {dept}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions className="corp-user-mgmt-dialog-actions">
            <Button 
              onClick={() => setEditOpen(false)} 
              variant="outlined"
              className="corp-user-mgmt-dialog-btn cancel"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit} 
              variant="contained" 
              color="primary"
              className="corp-user-mgmt-dialog-btn confirm"
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Dialog */}
        <Dialog 
          open={activateOpen} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ className: "corp-user-mgmt-dialog" }}
        >
          <DialogTitle className="corp-user-mgmt-dialog-title activate">
            <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Confirm Activation
          </DialogTitle>
          <DialogContent className="corp-user-mgmt-dialog-content">
            <Typography variant="h6" gutterBottom>
              Are you sure you want to activate this user?
            </Typography>
          </DialogContent>
          <DialogActions className="corp-user-mgmt-dialog-actions">
            <Button 
              onClick={() => setActivateOpen(false)} 
              variant="outlined"
              className="corp-user-mgmt-dialog-btn cancel"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmActivate} 
              variant="contained" 
              color="success"
              className="corp-user-mgmt-dialog-btn confirm activate"
            >
              Activate User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Deactivate Dialog */}
        <Dialog 
          open={deactivateOpen} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ className: "corp-user-mgmt-dialog" }}
        >
          <DialogTitle className="corp-user-mgmt-dialog-title deactivate">
            Confirm Deactivation
          </DialogTitle>
          <DialogContent className="corp-user-mgmt-dialog-content">
            <Typography variant="h6" gutterBottom color="error.main">
              Are you sure you want to deactivate this user?
            </Typography>
          </DialogContent>
          <DialogActions className="corp-user-mgmt-dialog-actions">
            <Button 
              onClick={() => setDeactivateOpen(false)} 
              variant="outlined"
              className="corp-user-mgmt-dialog-btn cancel"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDeactivate} 
              variant="contained" 
              color="error"
              className="corp-user-mgmt-dialog-btn confirm deactivate"
            >
              Deactivate User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Action Confirmation Dialog */}
        <Dialog 
          open={bulkActionConfirmOpen} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ className: "corp-user-mgmt-dialog" }}
        >
          <DialogTitle className="corp-user-mgmt-dialog-title">
            Confirm Bulk {bulkActionType === 'activate' ? 'Activation' : 'Deactivation'}
          </DialogTitle>
          <DialogContent className="corp-user-mgmt-dialog-content">
            <Typography variant="h6" gutterBottom>
              Are you sure you want to {bulkActionType === 'activate' ? 'activate' : 'deactivate'} {selectedRows.length} selected user(s)?
            </Typography>
          </DialogContent>
          <DialogActions className="corp-user-mgmt-dialog-actions">
            <Button 
              onClick={handleBulkActionCancel} 
              variant="outlined"
              className="corp-user-mgmt-dialog-btn cancel"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkActionConfirm} 
              variant="contained" 
              color={bulkActionType === 'activate' ? 'success' : 'error'}
              className={`corp-user-mgmt-dialog-btn confirm ${bulkActionType === 'activate' ? 'activate' : 'deactivate'}`}
            >
              Confirm {bulkActionType === 'activate' ? 'Activation' : 'Deactivation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Unlink Dialog */}
        <Dialog 
          open={unlinkOpen} 
          onClose={() => setUnlinkOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ className: "corp-user-mgmt-dialog" }}
        >
          <DialogTitle className="corp-user-mgmt-dialog-title">Confirm Unlink</DialogTitle>
          <DialogContent className="corp-user-mgmt-dialog-content">
            <Typography>
              Are you sure you want to unlink this user?
            </Typography>
          </DialogContent>
          <DialogActions className="corp-user-mgmt-dialog-actions">
            <Button 
              onClick={() => setUnlinkOpen(false)} 
              variant="outlined"
              className="corp-user-mgmt-dialog-btn cancel"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmUnlink} 
              variant="contained" 
              color="error"
              className="corp-user-mgmt-dialog-btn confirm deactivate"
            >
              Unlink
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog 
          open={deleteOpen} 
          onClose={() => setDeleteOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ className: "corp-user-mgmt-dialog" }}
        >
          <DialogTitle className="corp-user-mgmt-dialog-title">Delete User</DialogTitle>
          <DialogContent className="corp-user-mgmt-dialog-content">
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete this user? 
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action will permanently remove the user from the system and cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions className="corp-user-mgmt-dialog-actions">
            <Button 
              onClick={() => setDeleteOpen(false)} 
              variant="outlined"
              className="corp-user-mgmt-dialog-btn cancel"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete} 
              variant="contained" 
              color="error"
              className="corp-user-mgmt-dialog-btn confirm deactivate"
            >
              Delete User
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default CorporateUserDetailsComponent;